import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, CssBaseline, ThemeProvider } from '@mui/material';
import MainLayout from './components/layout/MainLayout';
import ConverterPage from './pages/ConverterPage';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RecentFilesPage from './pages/RecentFilesPage';
import TemplatesPage from './pages/TemplatesPage';
import theme from './theme';

const SESSION_EXPIRED_MESSAGE = 'Your session expired. Please log in again.';

const AppContent = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [loadingSession, setLoadingSession] = useState(true);
  const [expiresAt, setExpiresAt] = useState(null);
  const [sessionHours, setSessionHours] = useState(4);
  const [loginMessage, setLoginMessage] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [detectMessage, setDetectMessage] = useState('');
  const [detectingFile, setDetectingFile] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);

  useEffect(() => {
    let active = true;
    const interceptorId = axios.interceptors.response.use(response => response, error => {
      const requestUrl = error.config?.url || '';
      if (active && error.response?.status === 401 && !requestUrl.split('?')[0].endsWith('/api/login')) {
        setAuthenticated(false);
        setExpiresAt(null);
        setLoginMessage(SESSION_EXPIRED_MESSAGE);
      }
      return Promise.reject(error);
    });
    axios.get('/api/session').then(response => {
      if (!active) return;
      setAuthenticated(response.data.authenticated);
      setExpiresAt(response.data.expiresAt);
      setSessionHours(response.data.sessionHours || 4);
      setLoginMessage('');
    }).catch(() => {
      if (active) { setAuthenticated(false); setExpiresAt(null); }
    }).finally(() => { if (active) setLoadingSession(false); });
    return () => { active = false; axios.interceptors.response.eject(interceptorId); };
  }, []);

  useEffect(() => {
    if (!authenticated || !expiresAt) return undefined;
    const remainingTime = expiresAt - Date.now();
    if (remainingTime > 2147483647) return undefined;
    const timeout = window.setTimeout(() => {
      setAuthenticated(false);
      setExpiresAt(null);
      setLoginMessage(SESSION_EXPIRED_MESSAGE);
    }, Math.max(remainingTime, 0));
    return () => window.clearTimeout(timeout);
  }, [authenticated, expiresAt]);

  const handleLogin = sessionExpiry => {
    setExpiresAt(sessionExpiry);
    setAuthenticated(true);
    setLoginMessage('');
  };
  const handleLogout = () => { setAuthenticated(false); setExpiresAt(null); setLoginMessage(''); };
  const handleHomeFile = async file => {
    setPendingFile(file);
    setDetectMessage('');
    setDetectingFile(true);
    const data = new FormData();
    data.append('excelFile', file);
    try {
      const response = await axios.post('/api/detect', data);
      if (response.data.type) navigate(`/${response.data.type}`);
      else setDetectMessage("Couldn't tell the file type. Pick one below.");
    } catch (error) {
      setDetectMessage(error.response?.data?.error || 'Unable to read this file.');
    } finally { setDetectingFile(false); }
  };
  const consumePendingFile = useCallback(() => setPendingFile(null), []);
  const onHistoryChanged = useCallback(() => setHistoryVersion(value => value + 1), []);

  if (loadingSession) return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  if (!authenticated) return <LoginPage onLogin={handleLogin} message={loginMessage} sessionHours={sessionHours} />;

  return (
    <MainLayout onLogout={handleLogout} historyVersion={historyVersion}>
      <Routes>
        <Route path="/" element={<HomePage expiresAt={expiresAt} pendingFile={pendingFile} detectMessage={detectMessage} detectingFile={detectingFile} onSelectFile={handleHomeFile} historyVersion={historyVersion} />} />
        {['pricebook', 'inventory', 'catalog'].map(type => <Route key={type} path={`/${type}`} element={<ConverterPage key={type} type={type} pendingFile={pendingFile} onPendingFileConsumed={consumePendingFile} onHistoryChanged={onHistoryChanged} historyVersion={historyVersion} />} />)}
        <Route path="/files" element={<RecentFilesPage historyVersion={historyVersion} />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </MainLayout>
  );
};

function App() {
  return <ThemeProvider theme={theme}><CssBaseline /><Router><AppContent /></Router></ThemeProvider>;
}

export default App;
